import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, breadcrumbSchema, faqSchema } from "@/hooks/useJsonLd";
import {
  ArrowRight, TrendingUp, Award, Wrench, Briefcase, Crown, Hammer, GraduationCap,
  CheckCircle2, DollarSign, BookOpen, Clock,
} from "lucide-react";

const STAGES = [
  {
    id: "helper",
    title: "Helper / Apprentice",
    icon: Hammer,
    duration: "0-2 yrs",
    salary: "$40k – $60k",
    salaryHigh: 60000,
    description: "Pulling wire, mounting devices, learning the tools and the trade vocabulary.",
    nextSteps: [
      { text: "Complete this APM 140-hr course", link: "/enroll" },
      { text: "Earn FDNY S-95 cert", link: "/track/fdny-s95" },
      { text: "Log 1000+ field hours" },
    ],
    color: "#71717a",
  },
  {
    id: "s95",
    title: "FDNY S-95 Holder",
    icon: Award,
    duration: "1-3 yrs",
    salary: "$55k – $75k",
    salaryHigh: 75000,
    description: "Authorized to perform visual inspections of fire alarm systems in NYC buildings.",
    nextSteps: [
      { text: "Add S-98 supervision cert", link: "/nicet-exam-prep" },
      { text: "Begin NICET Level 1 prep", link: "/nicet-exam-prep" },
      { text: "Get OSHA 30" },
    ],
    color: "#dc2626",
  },
  {
    id: "s98",
    title: "FDNY S-98 Supervisor",
    icon: Briefcase,
    duration: "2-4 yrs",
    salary: "$65k – $90k",
    salaryHigh: 90000,
    description: "Supervises testing & maintenance of fire alarm + ancillary systems in NYC.",
    nextSteps: [
      { text: "Sit for NICET Level 1 exam", link: "/nicet-exam-prep" },
      { text: "Add F-53 (one-way voice) cert", link: "/track/fdny-f53" },
      { text: "Specialize in voice/EVACS", link: "/track/smoke-voice" },
    ],
    color: "#dc2626",
  },
  {
    id: "nicet1",
    title: "NICET Level I",
    icon: GraduationCap,
    duration: "2-5 yrs",
    salary: "$70k – $100k",
    salaryHigh: 100000,
    description: "Nationally-recognized Foundation cert. Opens doors to fire alarm contractors nationwide.",
    nextSteps: [
      { text: "Master ladder logic & SLC programming" },
      { text: "Begin NICET Level 2 prep", link: "/nicet-exam-prep" },
      { text: "Add S-13 standpipe cert", link: "/track/fdny-s13" },
    ],
    color: "#0ea5e9",
  },
  {
    id: "nicet2",
    title: "NICET Level II",
    icon: TrendingUp,
    duration: "4-7 yrs",
    salary: "$80k – $120k",
    salaryHigh: 120000,
    description: "Independently inspects, tests, and maintains. Designs systems under engineer review.",
    nextSteps: [
      { text: "Lead small project crews" },
      { text: "Add S-12 sprinkler cert", link: "/track/fdny-s12" },
      { text: "Mentor apprentices" },
    ],
    color: "#0ea5e9",
  },
  {
    id: "nicet3",
    title: "NICET Level III",
    icon: Wrench,
    duration: "6-10 yrs",
    salary: "$95k – $140k",
    salaryHigh: 140000,
    description: "Senior tech — designs full systems, performs acceptance tests, writes reports.",
    nextSteps: [
      { text: "Pursue NICET Level IV (senior engineering)" },
      { text: "Lead AHJ relationship" },
      { text: "Estimate + bid jobs", link: "/corporate" },
    ],
    color: "#16a34a",
  },
  {
    id: "lead",
    title: "Lead Technician / PM",
    icon: Crown,
    duration: "10+ yrs",
    salary: "$100k – $150k+",
    salaryHigh: 150000,
    description: "Project manager, foreman, or solo licensed contractor. Runs crews, owns jobs end-to-end.",
    nextSteps: [
      { text: "Open your own contracting firm", link: "/corporate" },
      { text: "Become an instructor", link: "/about-instructor" },
      { text: "Expand to BMS/automation", link: "/track/bms-elevator" },
    ],
    color: "#a16207",
  },
];

const AUDIENCE = [
  { label: "Electricians ready to add fire alarm to their toolkit", icon: "⚡" },
  { label: "Low-voltage technicians (security/data/CCTV)", icon: "🔌" },
  { label: "Fire alarm helpers stuck at apprentice wage", icon: "🔧" },
  { label: "Building engineers managing FACPs on their watch", icon: "🏢" },
  { label: "Property managers who need to understand inspections", icon: "📋" },
  { label: "Career changers tired of dead-end work", icon: "🎯" },
  { label: "Union apprentices preparing for journey-card exams", icon: "🪪" },
  { label: "FDNY S-95/S-98/S-12/S-13/F-53 candidates", icon: "🚒" },
];

export default function CareerRoadmap() {
  useDocumentMeta({
    title: "Fire Alarm Technician Career Roadmap & Salary Guide — APM Fire Alarm Exam Prep",
    description: "Complete career progression from Helper ($40k) to Lead Technician ($150k+). Maps every stage to FDNY certs and NICET levels. NYC + nationwide salaries.",
    image: "/og/og-career.png",
    path: "/career",
  });
  useJsonLd([
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Career Roadmap", url: "/career" },
    ]),
    faqSchema([
      { q: "How much do fire alarm technicians make in NYC?",
        a: "Salaries range from $40k–$60k for helpers/apprentices to $100k–$150k+ for lead technicians and project managers. NYC pays roughly 15-25% above the national average. NICET-certified techs earn the most." },
      { q: "How long does it take to become a NICET Level III technician?",
        a: "Typically 6-10 years from helper to NICET III, depending on study consistency and field experience. NICET requires documented work experience plus passing the written exam at each level." },
      { q: "Do I need to be a union member to work in fire alarm?",
        a: "No. Both union (IBEW Local 3 H-Division in NYC) and non-union shops hire fire alarm techs. Union typically pays higher hourly rates and benefits; non-union offers faster advancement." },
      { q: "Which certification should I get first — FDNY S-95 or NICET Level 1?",
        a: "For NYC work, FDNY S-95 first (visual inspection authority). For nationwide portability, NICET Level 1. Most career paths add both within 3 years." },
    ]),
  ]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main data-testid="career-roadmap-page">

        {/* Hero */}
        <section className="bg-zinc-950 text-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
            <div className="overline text-zinc-400 mb-3"><span className="red-bar" />Career trajectory</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]" data-testid="career-headline">
              From $40k helper to <span className="text-red-500">$150k+ lead tech.</span>
            </h1>
            <p className="text-zinc-300 mt-6 max-w-2xl text-lg leading-relaxed">
              Fire alarm is one of the last skilled trades where you can double your salary in 5 years
              without a 4-year degree. Here's the exact path — every stage, every cert, every paycheck.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 max-w-3xl">
              {[
                { n: "7", l: "Career stages" },
                { n: "10+", l: "Certifications" },
                { n: "$110k", l: "Median NICET III" },
                { n: "5 yrs", l: "To 6 figures" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-red-600 pl-3">
                  <div className="font-heading text-3xl font-bold tracking-tight">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Career roadmap stages */}
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="career-stages">
          <div className="overline mb-3"><span className="red-bar" />The path</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-10">
            7 stages from apprentice to expert
          </h2>

          <div className="relative">
            {/* Vertical timeline rail */}
            <div className="absolute left-[28px] top-0 bottom-0 w-0.5 bg-zinc-200" />

            {STAGES.map((stage, idx) => {
              const Icon = stage.icon;
              return (
                <div key={stage.id} className="relative pl-20 pb-10 last:pb-0 fade-in" data-testid={`stage-${stage.id}`}>
                  {/* Timeline node */}
                  <div
                    className="absolute left-0 top-0 w-14 h-14 grid place-items-center text-white shadow-lg"
                    style={{ backgroundColor: stage.color }}
                  >
                    <Icon className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                  {/* Card */}
                  <div className="card-tech p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                      <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">{stage.title}</h3>
                      <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{stage.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-heading text-2xl font-bold tracking-tight text-green-700" data-testid={`stage-${stage.id}-salary`}>
                        {stage.salary}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-700 leading-relaxed mb-4">{stage.description}</p>
                    <div className="border-t border-zinc-100 pt-3">
                      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Next steps to level up</div>
                      <ul className="space-y-1">
                        {stage.nextSteps.map((s, i) => (
                          <li key={i} className="text-sm flex items-start gap-2">
                            <ArrowRight className="w-3.5 h-3.5 text-red-600 mt-1 flex-shrink-0" />
                            {s.link ? (
                              <Link to={s.link} className="hover:text-red-600 underline decoration-zinc-300 underline-offset-2 hover:decoration-red-600 transition-colors" data-testid={`stage-${stage.id}-step-${i}-link`}>
                                {s.text}
                              </Link>
                            ) : (
                              <span>{s.text}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Salary table */}
        <section className="bg-zinc-50 py-20" data-testid="salary-table-section">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="overline mb-3"><span className="red-bar" />The numbers</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Fire alarm technician salary guide
            </h2>
            <p className="text-sm text-zinc-600 mb-10 max-w-2xl">
              Compiled from BLS data, NYC union scale (IBEW Local 3, USA), Indeed salary averages,
              and Trevor's 15+ years of hiring conversations. NYC metro pays roughly 15-25% above national average.
            </p>

            <div className="bg-white border-2 border-zinc-200">
              <div className="grid grid-cols-12 bg-zinc-950 text-white text-[10px] font-mono uppercase tracking-widest p-3">
                <div className="col-span-5">Position</div>
                <div className="col-span-3 text-center">Years experience</div>
                <div className="col-span-2 text-right">Low end</div>
                <div className="col-span-2 text-right">High end</div>
              </div>
              {STAGES.map((s, i) => (
                <div
                  key={s.id}
                  className={`grid grid-cols-12 p-4 border-t border-zinc-100 items-center ${i % 2 ? "bg-zinc-50/50" : ""}`}
                  data-testid={`salary-row-${s.id}`}
                >
                  <div className="col-span-5">
                    <div className="font-semibold text-sm">{s.title}</div>
                  </div>
                  <div className="col-span-3 text-center text-xs font-mono text-zinc-600">{s.duration}</div>
                  <div className="col-span-2 text-right font-mono text-sm">{s.salary.split(" – ")[0]}</div>
                  <div className="col-span-2 text-right font-mono text-sm font-bold text-green-700">{s.salary.split(" – ")[1]}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="card-tech p-5">
                <DollarSign className="w-5 h-5 text-green-600 mb-2" />
                <div className="font-heading text-2xl font-bold">+$60k</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Helper → NICET II uplift</div>
              </div>
              <div className="card-tech p-5">
                <TrendingUp className="w-5 h-5 text-red-600 mb-2" />
                <div className="font-heading text-2xl font-bold">12-18%</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Avg. raise per cert added</div>
              </div>
              <div className="card-tech p-5">
                <Clock className="w-5 h-5 text-zinc-700 mb-2" />
                <div className="font-heading text-2xl font-bold">~6 mo</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Avg. payback on this course</div>
              </div>
            </div>

            <p className="text-[11px] font-mono text-zinc-500 mt-6">
              Sources: BLS OES 2025, IBEW Local 3 wage sheet, Indeed/Glassdoor NYC metro averages,
              NICET salary survey 2024. Individual results vary based on shop, region, union vs non-union, and hustle.
            </p>
          </div>
        </section>

        {/* Who this course is for */}
        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="audience-section">
          <div className="overline mb-3"><span className="red-bar" />Who this is for</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            This course is perfect for
          </h2>
          <p className="text-sm text-zinc-600 mb-10 max-w-2xl">
            Built for working tradespeople who want a real career path — not a $5,000 exam prep you have to take time off work for.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {AUDIENCE.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border-2 border-zinc-100 hover:border-red-300 transition-colors" data-testid={`audience-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold">{a.label}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-zinc-950 text-white py-20" data-testid="career-cta-section">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-4">
              Start the climb.
            </h2>
            <p className="text-zinc-300 mb-10 max-w-2xl mx-auto">
              140 hours · 15 modules · 7 specialty tracks · interactive labs · AI tutor · printable certificate. $149 one-time, lifetime access.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/enroll">
                <button className="btn-primary" data-testid="career-enroll-btn">
                  Enroll — $149 lifetime <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/free-guide">
                <button className="btn-outline-light" data-testid="career-free-guide-btn">
                  <BookOpen className="w-4 h-4" /> Try 25 free S-98 questions
                </button>
              </Link>
            </div>
            <div className="mt-12 flex items-center justify-center gap-2 text-xs font-mono text-zinc-400">
              <span>Hiring crews?</span>
              <Link to="/corporate" className="underline hover:text-white" data-testid="career-corporate-link">
                See corporate training options →
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
